import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import classNames from 'classnames/bind';
import { RootState, useAppSelector } from '../../../store';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import { useTourState } from '../../tour/useTourState';
import { TourContext } from '../../tour/TourContext';
const GuidedTour = React.lazy(() => import('../../tour/GuidedTour'));
import { AIChatProvider } from '../../ai-chat/AIChatContext';
import AIChatWidget from '../../ai-chat/AIChatWidget';
import ImpersonationBanner from '../../common/impersonation-banner/ImpersonationBanner';
import { useGetAllCategoriesQuery } from '../../../api/categoriesApi';
import styles from './DashboardLayout.module.scss';

const cx = classNames.bind(styles);

interface DashboardLayoutProps {
  onLogout: () => void;
}

const TABLET_MAX = 1032;
const MOBILE_MAX = 768;

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ onLogout }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => window.innerWidth <= TABLET_MAX && window.innerWidth > MOBILE_MAX);
  const user = useAppSelector((state: RootState) => state.session.user);
  const { isImpersonating } = useAppSelector((s) => s.impersonation);

  // Prefetch categories for admin users so the exercise constructor loads instantly.
  useGetAllCategoriesQuery(undefined, { skip: user?.role !== 'ADMIN' });
  const tourState = useTourState();
  const contentRef = useRef<HTMLElement>(null);
  const location = useLocation();

  useEffect(() => {
    contentRef.current?.scrollTo(0, 0);
  }, [location.pathname]);

  // Auto-collapse sidebar on tablet widths
  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      if (w <= TABLET_MAX && w > MOBILE_MAX) {
        setSidebarCollapsed(true);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const ensureSidebarOpen = useCallback(() => {
    setSidebarCollapsed(false);
  }, []);

  const ensureSidebarClosed = useCallback(() => {
    setSidebarCollapsed(true);
  }, []);

  const isStudent = !isImpersonating && user?.role !== 'ADMIN' && user?.role !== 'CURATOR';

  return (
    <TourContext.Provider value={tourState}>
      <AIChatProvider>
        <div className={cx('dashboardLayout')}>
          <div className={cx('mainArea', { collapsed: sidebarCollapsed })}>
            <ImpersonationBanner />
            <TopBar
              user={user}
              onLogout={onLogout}
              onMenuToggle={toggleSidebar}
            />
            <main ref={contentRef} className={cx('content')}>
              <div className={cx('contentInner')}>
                <Outlet />
              </div>
            </main>
          </div>
          <Sidebar
            collapsed={sidebarCollapsed}
            onToggle={toggleSidebar}
            user={user}
          />
          {sidebarCollapsed && (
            <div
              className={cx('mobileOverlay')}
              onClick={() => {
                // Don't close sidebar if the tour is using it
                if (!tourState.isTourRunning) {
                  toggleSidebar();
                }
              }}
            />
          )}
          {isStudent && (
            <React.Suspense fallback={null}>
              <GuidedTour tourState={tourState} ensureSidebarOpen={ensureSidebarOpen} ensureSidebarClosed={ensureSidebarClosed} />
            </React.Suspense>
          )}
          <AIChatWidget />
        </div>
      </AIChatProvider>
    </TourContext.Provider>
  );
};

export default DashboardLayout;
